import {
  AfterViewInit,
  Component,
  Input,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { DeviceParameter } from '../../shared/models/device-parameter-model';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { FormControl, FormGroup } from '@angular/forms';
import { DeviceService } from '../../shared/services/device.service';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { UserInfoService } from 'src/app/core/auth/user-info/user-info.service';
import { MatButtonToggle } from '@angular/material/button-toggle';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { DeviceDetailService } from '../../shared/services/device-detail.service';
import {
  animate,
  animateChild,
  group,
  query,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';

@Component({
  selector: 'app-device-parameters',
  templateUrl: './device-parameters.component.html',
  styleUrls: ['./device-parameters.component.scss'],
  animations: [
    trigger('slideInOut', [
      state(
        '1',
        style({ height: '*', opacity: 1, transform: 'translateX(0)' })
      ),
      state(
        '0',
        style({ height: 0, opacity: 0, transform: 'translateX(-110%)' })
      ),
      state(
        'void',
        style({ height: 0, opacity: 0, transform: 'translateX(-110%)' })
      ),
      transition('0 => 1', [
        animate('200ms ease-out', style({ height: '*' })),
        animate('0ms', style({ opacity: 1 })),
        animate('400ms ease-out', style({ transform: 'translateX(0)' })),
      ]),
      transition('1 => 0', [
        animate('400ms ease-out', style({ transform: 'translateX(-110%)' })),
        animate('0ms', style({ opacity: 0 })),
        animate('200ms ease-out', style({ height: 0 })),
      ]),
    ]),
  ],
  providers: [
    DeviceDetailService,
    { provide: 'tablePaddingHeight', useValue: 262 },
    { provide: 'itemHeight', useValue: 52 },
    { provide: 'paginations', useValue: [5, 10, 25, 50, 100] },
  ],
})
export class DeviceParametersComponent implements OnInit, AfterViewInit {
  @Input() set deviceParametersData(value: DeviceParameter[] | undefined) {
    this.deviceParameters.data = value ?? [];
  }
  @Input() editable: boolean = false;

  protected mobile: boolean = false;
  protected modifying: boolean = false;
  protected showFilter: boolean = false;
  protected loading: boolean = false;

  private backup: DeviceParameter[] = [];
  private modified: DeviceParameter[] = [];

  protected readonly deviceParameters: MatTableDataSource<DeviceParameter> =
    new MatTableDataSource<DeviceParameter>();
  @ViewChild('deviceParametersPaginator', { static: true })
  private deviceParametersPaginator!: MatPaginator;
  @ViewChild('deviceParametersTable', { static: true })
  private deviceParametersSort = new MatSort();
  @ViewChild('deviceParametersGroup', { static: true })
  deviceParametersGroup!: MatButtonToggle;

  private readonly deviceParametersColumns: (keyof DeviceParameter)[] = [
    'parameterNumber',
    'parameterValue',
    'lastChangedBy',
    'lastUpdatedTimestamp',
    'important',
  ];
  protected deviceParametersColumnsToggle: (keyof DeviceParameter)[] = [
    'parameterNumber',
    'parameterValue',
    'important',
    'lastUpdatedTimestamp',
  ];
  protected get filteredDeviceParametersColumns(): (keyof DeviceParameter)[] {
    return this.deviceParametersColumns.filter((col) =>
      this.deviceParametersColumnsToggle.includes(col)
    );
  }

  protected readonly filters = new FormGroup({
    parameterNumber: new FormControl(''),
    parameterValue: new FormControl(''),
    lastChangedBy: new FormControl(''),
    lastUpdatedTimestamp: new FormControl(''),
    lastUpdatedTimestampStart: new FormControl(''),
    lastUpdatedTimestampEnd: new FormControl(''),
    important: new FormControl(''),
  });

  protected clearFilter(name: string, $event: MouseEvent) {
    $event.stopPropagation();
    this.filters.get(name)?.setValue('');
  }

  protected emptyFilter(name: string): boolean {
    return this.filters.get(name)?.value === '';
  }

  constructor(
    private deviceService: DeviceService,
    private breakpointObserver: BreakpointObserver,
    private deviceDetailService: DeviceDetailService
  ) {
    this.deviceParameters.filterPredicate = this.tableFilter();
    this.breakpointObserver.observe([Breakpoints.HandsetPortrait]).subscribe({
      next: (result) => {
        this.mobile = result.matches;
        if (result.matches) {
          this.deviceParametersColumnsToggle = [
            'parameterNumber',
            'parameterValue',
            'important',
            'lastUpdatedTimestamp',
          ];
        }
        if (this.deviceParametersPaginator) {
          this.deviceDetailService.calculatePagination(
            this.deviceParametersPaginator
          );
          this.deviceParameters.paginator = this.deviceParametersPaginator;
        }
      },
    });
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.deviceDetailService.calculatePagination(
        this.deviceParametersPaginator
      );
      this.deviceParameters.paginator = this.deviceParametersPaginator;
    }, 100);
  }

  ngOnInit(): void {
    this.deviceParameters.paginator = this.deviceParametersPaginator;
    this.deviceParameters.sort = this.deviceParametersSort;
    this.filters.valueChanges.subscribe((filter) => {
      this.deviceParameters.filter = JSON.stringify(filter);
    });
  }

  private tableFilter(): (data: any, filter: string) => boolean {
    return (data: DeviceParameter, filter: string): boolean => {
      const searchTerms = JSON.parse(filter);
      return Object.keys(searchTerms)
        .filter((s) => searchTerms[s])
        .every((key) => {
          if (key === 'lastUpdatedTimestampStart') {
            return data.lastUpdatedTimestamp >= searchTerms[key];
          }
          if (key === 'lastUpdatedTimestampEnd') {
            return data.lastUpdatedTimestamp <= searchTerms[key];
          }
          const value = data[key as keyof DeviceParameter]
            .toString()
            .toLowerCase();
          return value.includes(searchTerms[key].toString().toLowerCase());
        });
    };
  }

  protected onImportantToggle(
    event: MatSlideToggleChange,
    element: DeviceParameter
  ) {
    event.source.checked = event.checked;
    element.important = event.checked;
    this.modified = this.modified.filter(
      (item) => item.parameterNumber !== element.parameterNumber
    );
    this.modified.push(element);
  }

  protected onChangeClick() {
    if (!this.modifying) {
      this.backup = JSON.parse(JSON.stringify(this.deviceParameters.data));
      this.modified = [];
      this.modifying = true;
      if (!this.deviceParametersColumnsToggle.includes('important')) {
        this.deviceParametersColumnsToggle = [
          ...this.deviceParametersColumnsToggle,
          'important',
        ];
      }
    } else if (this.modified.length) {
      this.loading = true;
      this.deviceService
        .updateDeviceParameters(this.modified)
        .subscribe({
          error: () => {
            this.deviceParameters.data = this.backup;
          },
        })
        .add(() => {
          this.loading = false;
        });
      this.clearModify();
    } else {
      this.clearModify();
    }
  }

  protected onCancelClick() {
    this.deviceParameters.data = this.backup;
    this.clearModify();
  }

  private clearModify() {
    this.backup = [];
    this.modified = [];
    this.modifying = false;
  }

  protected get modifyEnabled(): boolean {
    return Boolean(this.editable && this.deviceParameters?.data?.length);
  }

  protected get modifyMode(): boolean {
    return this.editable && this.modifying;
  }
}
